import { Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import React from 'react';
import { ActivityMetricType } from 'shared/models/activity-metric';

import { buildDistributedHorizontalBarOption } from 'shared/common/charts/options';

import { echarts, ReactEChartsCore } from '../../common/charts/echarts-core';
import { usePortalTooltip } from '../../common/charts/tooltip';
import {
    defaultFunnelSelected,
    Field,
    FieldsSelected,
    funnelMetricFields,
    funnelMetrics,
    getChartHeight,
    getFieldColors,
    getFieldLabel
} from '../../common/metrics';
import { useChartExport } from '../../hooks/use-chart-export';
import { useLocalStorage } from '../../hooks/use-local-storage';
import { ChartExportButtons } from '../charts/chart-export-buttons';
import { Legend } from './legend';

export const FunnelMetricsChart: React.FC<{
    metrics?: Array<{ kind: ActivityMetricType; count: number; cost: number }>;
}> = ({ metrics }) => {
    const [selectedFields, setSelectedFields] = useLocalStorage<FieldsSelected>(
        'funnel-metrics-fields',
        defaultFunnelSelected
    );
    const [hoveredField, setHoveredField] = React.useState<Field | undefined>(undefined);
    const chartExport = useChartExport({ title: 'funnel-metrics' });

    const metricsByField = funnelMetrics(metrics);
    const fields = funnelMetricFields.filter((f) => selectedFields[f]);
    const categories = fields.map(getFieldLabel);
    const data = fields.map((f) => metricsByField?.[f] ?? 0);

    const colors = getFieldColors(fields, hoveredField);

    const labelFormatter = React.useCallback(
        (value: number, index: number) => {
            const field = fields[index];
            const funnelIndex = funnelMetricFields.indexOf(field);
            const baselineField = funnelIndex > 0 ? funnelMetricFields[funnelIndex - 1] : undefined;
            const baseline = baselineField ? metricsByField?.[baselineField] : undefined;
            const percent = value && baseline ? ` (${Math.round((value / baseline) * 100)}%)` : '';
            return `${value}${percent}`;
        },
        [fields, metricsByField]
    );

    const tooltip = usePortalTooltip(
        {
            formatter: (raw: any) => {
                const params = Array.isArray(raw) ? raw[0] : raw;
                const rawValue = params?.value;
                const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
                return `
                    <div class="metrics-tooltip">
                        <div class="metrics-tooltip-header">${params?.name ?? ''}</div>
                        <div class="metrics-tooltip-value">${value ?? 0}</div>
                    </div>
                `;
            },
            trigger: 'item'
        },
        []
    );

    const chartOptions = React.useMemo(() => {
        if (!fields.length || !categories.length) {
            return undefined;
        }
        const chartData = fields.map((field, index) => ({
            name: getFieldLabel(field),
            value: data[index]
        }));
        return buildDistributedHorizontalBarOption({
            categories,
            colors,
            data: chartData,
            labelFormatter,
            tooltip
        });
    }, [categories, colors, data, fields, labelFormatter, tooltip]);

    const canExport = Boolean(metrics && chartOptions && categories.length > 0);

    const chart =
        !!metrics && !!chartOptions ? (
            <ReactEChartsCore
                ref={chartExport.chartRef}
                echarts={echarts}
                option={chartOptions}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: getChartHeight(categories?.length ?? 0) }}
                opts={{ renderer: 'svg' }}
            />
        ) : (
            <Skeleton variant="rect" />
        );

    return (
        <div className="chart-container">
            <div className="panel-header">
                <div className="panel-title">
                    <Typography variant="h5">Funnel Metrics</Typography>
                </div>
                <div className="panel-options">
                    <ChartExportButtons
                        disabled={!canExport}
                        isExporting={chartExport.isExporting}
                        onExportPng={chartExport.exportAsPng}
                        onExportSvg={chartExport.exportAsSvg}
                    />
                </div>
            </div>
            <div className="panel-content">
                <div className="chart">{chart}</div>
                <Legend
                    fields={funnelMetricFields}
                    selected={selectedFields}
                    onFieldHover={setHoveredField}
                    onUpdateSelected={setSelectedFields}
                />
            </div>
        </div>
    );
};
